import { useEffect, useState } from "react";
import ModalV2 from "../../ModalV2";
import useModal from "../../../hooks/useModal";
import useAbis from "../../../hooks/useAbis";
import toast from "react-hot-toast";
import { Button } from "@oc-labs/wt-storybook";
import { X, Sparkles } from "lucide-react";
import PropTypes from "prop-types";

export default function GenerateAbiModal({ onGenerated, sourceCodes = [] }) {
  const { close, isOpen } = useModal("generate-abi-modal");
  const openUpsertModal = useModal("upsert-abi-modal").open;
  const { adminGenerateAbiFromSource } = useAbis();
  const [sourceCodeName, setSourceCodeName] = useState("");
  const [name, setName] = useState("");
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSourceCodeName("");
      setName("");
      setGenerating(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (sourceCodeName && !name) {
      const stripped = sourceCodeName.replace(/\.[^.]+$/, "");
      setName(stripped);
    }
  }, [sourceCodeName]);

  const handleGenerate = async () => {
    if (!sourceCodeName) {
      toast.error("Pick a source code file");
      return;
    }
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setGenerating(true);
    try {
      const result = await adminGenerateAbiFromSource(sourceCodeName);
      toast.success("ABI generated");
      const abi = result?.abi ?? result?.data?.abi ?? result;
      close();
      openUpsertModal({
        __isNew: true,
        name,
        abi,
      });
      onGenerated?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to generate ABI");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <ModalV2 name="generate-abi-modal" className="w-[min(560px,95vw)]">
      <div className="p-6 border-b border-gray-300 bg-gradient-to-br from-purple-600 to-pink-600 rounded-xl">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <h1 className="text-white text-2xl font-black flex items-center gap-2">
              <Sparkles size={22} /> Auto-generate ABI
            </h1>
            <p className="text-purple-100 text-sm">
              Generate the ABI from existing source code.
            </p>
          </div>
          <button className="text-gray-200 hover:text-white" onClick={() => close()}>
            <X />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <label className="flex flex-col gap-2">
          <p className="text-gray-900 text-sm font-medium">Source Code</p>
          <select
            className="w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 px-4 text-base font-normal"
            value={sourceCodeName}
            onChange={(e) => setSourceCodeName(e.target.value)}
          >
            <option value="">Select source code…</option>
            {sourceCodes.map((sc) => (
              <option key={sc.name} value={sc.name}>
                {sc.name}
              </option>
            ))}
          </select>
          {sourceCodes.length === 0 && (
            <p className="text-xs text-gray-500">
              No source code uploaded yet. Add one in Source Code first.
            </p>
          )}
        </label>

        <label className="flex flex-col gap-2">
          <p className="text-gray-900 text-sm font-medium">ABI Name</p>
          <input
            className="form-input w-full rounded-lg text-gray-900 focus:outline-0 focus:ring-2 focus:ring-primary border border-gray-300 bg-white h-12 placeholder:text-gray-400 px-4 text-base font-normal"
            placeholder="e.g. ERC20"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="tertiary" onClick={() => close()}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleGenerate} disabled={generating}>
            {generating ? "Generating…" : "Generate"}
          </Button>
        </div>
      </div>
    </ModalV2>
  );
}

GenerateAbiModal.propTypes = {
  onGenerated: PropTypes.func,
  sourceCodes: PropTypes.array,
};
